import CreateButton from "../components/CreateButton"
import { getModuleSubmission, moduleInfoFields, modulePeopleChanged } from './moduleEditorState';
import {
    BlockTypeSelect,
    BoldItalicUnderlineToggles,
    CreateLink,
    diffSourcePlugin,
    DiffSourceToggleWrapper,
    headingsPlugin,
    imagePlugin,
    InsertImage,
    linkDialogPlugin,
    linkPlugin,
    listsPlugin,
    ListsToggle,
    MDXEditor,
    quotePlugin,
    Separator,
    tablePlugin,
    thematicBreakPlugin,
    toolbarPlugin,
    UndoRedo
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { useEffect, useRef, useState } from "react";
import Markdown from 'react-markdown';
import { useNavigate, useParams } from "react-router-dom";
import { ReactSortable } from 'react-sortablejs';
import rehypeRaw from 'rehype-raw';
import api from "../api";
import { ReactComponent as CloseIcon } from '../assets/images/icons/ic-close.svg';
import { ButtonStatus, FileType, LessonType } from "../common/constants";
import { Each } from "../common/Each";
import Breadcrumb from "../components/Breadcrumb";
import StatusBadge from '../components/StatusBadge';
import Button from "../components/Button";
import Checkbox from "../components/Checkbox";
import AlertDialog from "../components/dialogs/AlertDialog";
import Dialog from "../components/dialogs/Dialog";
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import LessonEditor from "../components/LessonEditor";
import Table from '../components/Table';
import TextInput from "../components/TextInput";
import Uploader from '../components/Uploader';
import { DialogStatus } from "../enums";
import typo from "../typography.module.css";
import { formatSeconds, formatTimeV2 } from '../utils';
import styles from "./ModuleEditor.module.css";

const ModuleEditor = () => {

    const navigate = useNavigate()
    const [course, setCourse] = useState(null)
    const [edition, setEdition] = useState(null)
    const [module, setModule] = useState(null)
    const { courseId, editionId, moduleId } = useParams()
    const [alert, setAlert] = useState({ open: false, title: '', text: '', actions: [], status: DialogStatus.Default })
    const [savingSection, setSavingSection] = useState(null)
    const [savedModule, setSavedModule] = useState(null)
    const [saveDisabled, setSaveDisabled] = useState(true)
    const [teachers, setTeachers] = useState([])
    const [tutors, setTutors] = useState([])
    const [loadingTeacherResults, setLoadingTeacherResults] = useState(false)
    const [loadingTutorResults, setLoadingTutorResults] = useState(false)
    const [teacherSearch, setTeacherSearch] = useState("")
    const [tutorSearch, setTutorSearch] = useState("")
    const teacherSearchRequestRef = useRef(0)
    const tutorSearchRequestRef = useRef(0)
    const [lesson, setLesson] = useState(null)
    const [uploaderReset, setUploaderReset] = useState(false)
    const [lessonDelete, setLessonDelete] = useState(null)
    const [forceDeleteLesson, setForceDeleteLesson] = useState(null)
    const [sortedLessons, setSortedLessons] = useState([])
    const [ordering, setOrdering] = useState(false)
    const [orderedLessons, setOrderedLessons] = useState([])
    const [savingOrder, setSavingOrder] = useState(false)

    const [editMode, setEditMode] = useState(moduleId === 'create' ? true : false)

    const columns = [
        {
            key: 'id',
            label: 'ID',
            type: 'number',
            pinned: false
        },
        {
            key: 'room_name',
            label: 'STANZA',
            type: 'string',
            pinned: false
        },
        {
            key: 'name',
            label: 'NOME',
            type: 'string',
            pinned: false
        },
        {
            key: 'position',
            label: 'POSIZIONE',
            type: 'number',
            pinned: false
        },
        {
            key: 'date',
            label: 'DATA',
            type: 'date',
            pinned: false
        },
        {
            key: 'hour',
            label: 'ORA',
            type: 'string',
            pinned: false
        },
        {
            key: '_description',
            label: 'DESCRIZIONE',
            type: 'string',
            pinned: false
        },
        {
            key: 'terminated_at',
            label: 'TERMINATA',
            type: 'datetime',
            pinned: false
        }
    ]

    useEffect(() => {
        if (module) {
            setSortedLessons(module.lessons.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)))
            setOrderedLessons(module.lessons.sort((a, b) => a.position - b.position))
        }
    }, [module])

    useEffect(() => {
        const getCourse = async () => {
            try {
                const course = await api.get(`/admin/courses/${courseId}`)
                setCourse(course)
            }
            catch (e) {
                console.error(e)
            }
        }

        const getModule = async (module_id) => {
            try {
                let module = await api.get(`/admin/modules/${module_id}`)
                setModule(module)
                setSavedModule(structuredClone(module))
                setEdition(module.edition)
                setCourse(module.edition.course)
                setSortedLessons(module.lessons.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)))
            }
            catch (e) {
                console.error(e)
            }
        }

        const getEdition = async () => {
            try {
                let edition = await api.get(`/admin/editions/${editionId}`)
                setEdition(edition)
            }
            catch (e) {
                console.error(e)
            }
        }

        if (moduleId && moduleId !== 'create') {
            getModule(moduleId)
        }

        if (moduleId && moduleId === 'create') {
            getCourse()
            getEdition()

            setModule(
                {
                    name: '',
                    description: '',
                    thumbnail: '',
                    duration: 0,
                    tags: [],
                    teachers: [],
                    tutors: [],
                    lessons: [],
                    time_restricted: true
                }
            )
        }
    }, [moduleId])

    useEffect(() => {
        if (module) {
            setSaveDisabled(module.name.trim() === '' || module.description.trim() === '' || !module.thumbnail || module.thumbnail.trim() === '')
        }
        if (module && edition && !module.thumbnail && moduleId === 'create') {
            setModule((prev) => {
                prev.thumbnail = edition.thumbnail
                return { ...prev }
            })
        }
    }, [module])

    const infoFields = moduleInfoFields
    const changedInfo = moduleId === 'create' || (savedModule && infoFields.some(key => module?.[key] !== savedModule[key]))
    const peopleChanged = (key) => modulePeopleChanged(module, savedModule, key)
    const saveButton = (section, changed) => changed && (
        <Button accentColor="var(--primary)" additionalClass={styles.cardSave}
            status={savingSection === section ? ButtonStatus.Loading : null}
            disabled={!!savingSection || (section === 'info' && saveDisabled)}
            onClick={() => save(section)}>
            {moduleId === 'create' ? 'Crea modulo' : 'Salva modifiche'}
        </Button>
    )

    const save = async (section) => {
        setSavingSection(section)
        const fields = section === 'info' ? infoFields : [section]
        // The API replaces all associations: send persisted values for the other cards.
        const submitted = getModuleSubmission(module, savedModule, section, moduleId === 'create')
        try {

            const body = {
                edition_id: editionId,
                name: submitted.name,
                description: submitted.description,
                duration: isNaN(parseInt(submitted.duration)) ? 0 : parseInt(submitted.duration),
                thumbnail: submitted.thumbnail,
                time_restricted: submitted.time_restricted,
                tags: JSON.stringify(submitted.tags.map(t => t.id)),
                teachers: JSON.stringify(
                    [...submitted.teachers.map(t => t.id), ...submitted.tutors.map(t => t.id)]
                )
            }

            const response = (moduleId === "create") ?
                await api.post(`/admin/modules`, body) :
                await api.put(`/admin/modules/${moduleId}`, body)


            if (moduleId === "create") {
                navigate(`/courses/${courseId}/editions/${editionId}/modules/${response.id}`)
                return;
            }
            setSavedModule(structuredClone(response))
            setModule(prev => ({
                ...prev,
                ...Object.fromEntries(fields.map(key => [key, JSON.stringify(prev[key]) === JSON.stringify(submitted[key]) ? response[key] : prev[key]]))
            }))
            if (section === 'info') {
                setEditMode(false)
                setUploaderReset(o => !o)
            }
            setAlert({
                open: true,
                title: 'Operazione Completata',
                text: 'I dati sono stati aggiornati con successo',
                status: DialogStatus.Success,
                actions: [
                    {
                        label: 'CHIUDI',
                        onClick: () => {
                            setAlert((prev) => {
                                prev.open = false
                                return { ...prev }
                            })
                        }
                    }
                ]
            })

        }
        catch (e) {
            console.error(e)
            setAlert({
                open: true,
                title: 'Operazione Fallita',
                text: e.detail,
                status: DialogStatus.Error,
                actions: [
                    {
                        label: 'OK',
                        onClick: () => {
                            setAlert((prev) => {
                                prev.open = false
                                return { ...prev }
                            })
                        }
                    }
                ]
            })
        }
        finally {
            setSavingSection(null)
        }
    }

    const addTeacherToModule = (teacher) => {
        if (module.teachers.some((currentTeacher) => currentTeacher.id === teacher.id)) {
            return
        }

        setModule((prev) => {
            return { ...prev, teachers: [...prev.teachers, teacher] }
        })
        setTeacherSearch('')
        setTeachers([])
    }

    const addTutorToModule = (tutor) => {
        if (module.tutors.some((currentTutor) => currentTutor.id === tutor.id)) {
            return
        }

        setModule((prev) => {
            return { ...prev, tutors: [...prev.tutors, tutor] }
        })
        setTutorSearch('')
        setTutors([])
    }

    const removeTeacherFromModule = (teacherId) => {
        setModule((prev) => ({
            ...prev,
            teachers: prev.teachers.filter((teacher) => teacher.id !== teacherId)
        }))
    }

    const removeTutorFromModule = (tutorId) => {
        setModule((prev) => ({
            ...prev,
            tutors: prev.tutors.filter((tutor) => tutor.id !== tutorId)
        }))
    }

    const deleteLesson = async (lesson_id, force = false) => {
        try {
            const res = await api.delete(`/admin/lessons/${lesson_id}?force=${force}`)
            if (res && res.need_force) {
                setForceDeleteLesson(lesson_id)
            }
        } catch (e) {
            console.error(e)
        }
    }

    const getLessonDurationSeconds = (lesson) => {
        if (!lesson) {
            return 0
        }

        const isRecorded = lesson.type === LessonType.Recorded || lesson.type === 'recorded'
        if (isRecorded) {
            const videoDurationSeconds = Number(lesson.video_duration) || 0
            return videoDurationSeconds
        }

        const lessonEnd = lesson.ended_at || lesson.ends_at
        if (!lesson.starts_at || !lessonEnd) {
            return 0
        }

        const durationSeconds = (new Date(lessonEnd) - new Date(lesson.starts_at)) / 1000
        return Number.isFinite(durationSeconds) && durationSeconds > 0 ? durationSeconds : 0
    }

    const getModuleDurationSeconds = (lessons = []) => {
        return lessons.reduce((acc, currentLesson) => {
            return acc + getLessonDurationSeconds(currentLesson)
        }, 0)
    }

    const formatModuleDuration = (durationSeconds) => {
        return formatSeconds(Number(durationSeconds) || 0)
    }

    useEffect(() => {
        const query = teacherSearch.trim()
        teacherSearchRequestRef.current += 1
        const requestId = teacherSearchRequestRef.current

        if (query.length < 2) {
            setTeachers([])
            setLoadingTeacherResults(false)
            return
        }

        const timeout = setTimeout(async () => {
            setLoadingTeacherResults(true)
            try {
                const response = await api.get(`/admin/teachers?orderBy=-surname&page=0&per=20&complete=false&q=${encodeURIComponent(query)}`)
                const results = Array.isArray(response?.teachers) ? response.teachers : []
                if (teacherSearchRequestRef.current === requestId) {
                    setTeachers(results.filter((teacher) => teacher.role === 'teacher'))
                }
            }
            catch (e) {
                console.error(e)
                if (teacherSearchRequestRef.current === requestId) {
                    setTeachers([])
                }
            }
            if (teacherSearchRequestRef.current === requestId) {
                setLoadingTeacherResults(false)
            }
        }, 300)

        return () => clearTimeout(timeout)
    }, [teacherSearch])

    useEffect(() => {
        const query = tutorSearch.trim()
        tutorSearchRequestRef.current += 1
        const requestId = tutorSearchRequestRef.current

        if (query.length < 2) {
            setTutors([])
            setLoadingTutorResults(false)
            return
        }

        const timeout = setTimeout(async () => {
            setLoadingTutorResults(true)
            try {
                const response = await api.get(`/admin/teachers?orderBy=-surname&page=0&per=20&complete=false&q=${encodeURIComponent(query)}`)
                const results = Array.isArray(response?.teachers) ? response.teachers : []
                if (tutorSearchRequestRef.current === requestId) {
                    setTutors(results.filter((teacher) => teacher.role === 'tutor'))
                }
            }
            catch (e) {
                console.error(e)
                if (tutorSearchRequestRef.current === requestId) {
                    setTutors([])
                }
            }
            if (tutorSearchRequestRef.current === requestId) {
                setLoadingTutorResults(false)
            }
        }, 300)

        return () => clearTimeout(timeout)
    }, [tutorSearch])

    const filteredTeachers = teachers
    const filteredTutors = tutors

    return (
        <HeaderFooterLayout hideFooter>
            <div className={styles.container}>
                {module &&

                    <div className={styles.section}>
                        <div className={styles.sectionInner}>
                            <Breadcrumb items={[
                                { label: 'Corsi', to: '/courses' },
                                course && { label: course.name, to: `/courses/${courseId}` },
                                edition && { label: edition.name, to: `/courses/${courseId}/editions/${editionId}` },
                                { label: moduleId === 'create' ? 'Nuovo modulo' : module.name }
                            ]} />
                            {editMode &&
                                <div className={styles.infoSection}>
                                    <div className={styles.modulesHeader}>
                                        <div className={styles.sectionTitle}>Modifica informazioni</div>
                                        <div className={styles.cardActions}>
                                            {moduleId !== 'create' && <Button appearance="subtle" disabled={!!savingSection} onClick={() => {
                                                setModule(prev => ({ ...prev, ...Object.fromEntries(infoFields.map(key => [key, savedModule[key]])) }))
                                                setUploaderReset(o => !o)
                                                setEditMode(false)
                                            }}>Annulla</Button>}
                                            {saveButton('info', changedInfo)}
                                        </div>
                                    </div>
                                    <div className={styles.infoEditorContent}>
                                        <div className={styles.row}>
                                            <div className={styles.column}>
                                                <div className={styles.columnInner}>
                                                    <TextInput appearance="modern" type="text" value={module.name} placeholder="Nome" onKeyUp={(value) => {
                                                        setModule((prev) => {
                                                            prev.name = value
                                                            return { ...prev }
                                                        })
                                                    }} />
                                                </div>

                                                <div className={styles.columnInner}>
                                                    <div className={typo.caption}>DESCRIZIONE</div>
                                                    <MDXEditor
                                                        className={styles.editor}
                                                        contentEditableClassName={styles.editorContent}
                                                        markdown={module.description}
                                                        onChange={(value) => {
                                                            setModule((prev) => {
                                                                prev.description = value
                                                                return { ...prev }
                                                            })
                                                        }}
                                                        placeholder={"Descrizione del modulo..."}
                                                        plugins={[
                                                            headingsPlugin(),
                                                            linkPlugin(),
                                                            listsPlugin(),
                                                            quotePlugin(),
                                                            thematicBreakPlugin(),
                                                            linkDialogPlugin(),
                                                            diffSourcePlugin(),
                                                            imagePlugin(),
                                                            tablePlugin(),
                                                            toolbarPlugin({
                                                                toolbarContents: () => (
                                                                    <>
                                                                        {' '}
                                                                        <UndoRedo />
                                                                        <Separator />
                                                                        <BoldItalicUnderlineToggles />
                                                                        <Separator />
                                                                        <InsertImage />
                                                                        <ListsToggle />
                                                                        <CreateLink />
                                                                        <Separator />
                                                                        <BlockTypeSelect />
                                                                        <DiffSourceToggleWrapper options={["rich-text", "source"]} />
                                                                    </>
                                                                )
                                                            })
                                                        ]}
                                                    />
                                                </div>

                                                {(module?.edition?.type === "live" || edition?.type === 'live') && (
                                                    <div className={styles.columnInner}>
                                                        <Checkbox label="Applica restrizioni d'accesso" labelPosition='end' value={module.time_restricted}
                                                            style={{ width: 'fit-content', padding: 0 }}
                                                            onChange={(checked) => {
                                                                setModule((prev) => {
                                                                    prev.time_restricted = checked
                                                                    return { ...prev }
                                                                })
                                                            }} />
                                                        <div className={typo.caption}>Applicando le restrizioni d'accesso il modulo sarà disponibile agli studenti da 5g prima dell'inizio della prima lezione.</div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className={styles.column}>
                                                <div className={styles.columnInner}>
                                                    <TextInput appearance="modern" disabled={module?.thumbnail?.startsWith('blob:')} type="text" value={module?.thumbnail} placeholder="Thumbnail URL" onKeyUp={(value) => {
                                                        setModule((prev) => {
                                                            prev.thumbnail = value ? value : ''
                                                            return { ...prev }
                                                        })
                                                    }} />

                                                    <Uploader
                                                        crop={true}
                                                        preview={module?.thumbnail}
                                                        reset={uploaderReset}
                                                        multiple={false}
                                                        type={FileType.Image}
                                                        onComplete={(uploads) => {
                                                            setModule((prev) => {
                                                                prev.thumbnail = uploads[0].url
                                                                return { ...prev }
                                                            })
                                                        }}
                                                        onDelete={() => {
                                                            setModule((prev) => {
                                                                prev.thumbnail = ""
                                                                return { ...prev }
                                                            })
                                                        }}
                                                    />
                                                </div>
                                                <div className={styles.columnInner}>
                                                    <div className={typo.caption}>DURATA</div>
                                                    <div className={typo.body}><strong>{formatModuleDuration(module.duration)}</strong></div>
                                                    <div className={typo.caption}>La durata di un modulo è calcolata automaticamente sulla base delle lezioni contenute nel modulo.</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                            {!editMode &&
                                <div className={styles.infoSection}>
                                    <div className={styles.info} style={!module.thumbnail ? { gridTemplateColumns: '1fr' } : undefined}>
                                        {module.thumbnail &&
                                            <div className={styles.infoVisual}>
                                                <img src={module.thumbnail} alt='module-thumbnail' className={styles.infoThumbnail} />
                                            </div>
                                        }
                                        <div className={styles.infoInner}>
                                            <div className={styles.infoHeaderRow}>
                                                <div className={styles.infoTitleBlock}>
                                                    <div className={styles.infoName}>{module.name}</div>
                                                    <div className={styles.infoDescription}>
                                                        <Markdown rehypePlugins={[rehypeRaw]}>{module.description}</Markdown>
                                                    </div>
                                                </div>
                                                <div className={styles.infoRestrictions}>
                                                    <StatusBadge appearance="soft" color={(module?.edition?.type || edition?.type) === "live" ? 'var(--primary)' : 'var(--secondary)'} label={(module?.edition?.type || edition?.type) === "live" ? "LIVE" : "ON DEMAND"} />
                                                    {(module?.edition?.type || edition?.type) === "live" && (
                                                        module.time_restricted
                                                            ? <StatusBadge appearance="soft" color="var(--sf-red)" label={"RESTRIZIONI ATTIVE"} />
                                                            : <StatusBadge appearance="soft" color="var(--sf-green)" label={"NESSUNA RESTRIZIONE"} />
                                                    )}
                                                    <Button appearance="subtle" onClick={() => setEditMode(true)}>Modifica</Button>
                                                </div>
                                            </div>
                                            <div className={styles.infoMetaList}>
                                                <div className={styles.infoMetaRow}>
                                                    <div className={styles.infoMetaLabel}>Durata</div>
                                                    <div className={styles.infoMetaValue}>{formatModuleDuration(module.duration)}</div>
                                                </div>
                                                <div className={styles.infoMetaRow}>
                                                    <div className={styles.infoMetaLabel}>Lezioni</div>
                                                    <div className={styles.infoMetaValue}>{module.lessons.length}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }

                            {edition && course &&
                                <div className={`${styles.modules} ${styles.tableCard}`}>
                                    <div className={styles.modulesHeader}>
                                        <div className={styles.sectionTitle}>Lezioni · {module.lessons.length}</div>
                                        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
                                            {module.lessons.length > 1 &&
                                                <Button
                                                    appearance="subtle"
                                                    disabled={moduleId === "create"}
                                                    onClick={() => {
                                                        setOrdering(!ordering)
                                                    }}>
                                                    Riordina
                                                </Button>
                                            }
                                            <CreateButton disabled={moduleId === "create"} onClick={() => {
                                                    setLesson({ name: '', type: LessonType.Live, description: '', thumbnail: module.thumbnail, tags: [], module_id: moduleId, course: { id: course.id, name: course.name }, edition: { id: edition.id, name: edition.name } })
                                                }}>
                                                Aggiungi lezione
                                            </CreateButton>
                                        </div>
                                    </div>
                                        <Table
                                            appearance='minimal'
                                            style={{ border: 0, borderRadius: 0, boxShadow: 'none', marginTop: 0 }}
                                            columns={columns}
                                            elements={sortedLessons.map((l) => {
                                                l._description = l.description.slice(0, 60) + (l.description.length > 60 ? '...' : '')
                                                l.date = l.starts_at
                                                l.hour = `${formatTimeV2(l.starts_at)} - ${formatTimeV2(l.ends_at)}`
                                                return l
                                            })}
                                            defaultSort={{ key: 'date', direction: 'desc' }}
                                            onRowClick={(lesson) => {
                                                setLesson({ ...lesson, course: { id: course.id, name: course.name }, edition: { id: edition.id, name: edition.name } })
                                            }}
                                        />
                                </div>
                            }

                            <div className={styles.sectionRow}>
                                <div className={styles.modules}>
                                    <div className={styles.modulesHeader}>
                                        <div className={styles.sectionTitle}>Docenti · {module.teachers.length}</div>
                                        {moduleId !== 'create' && peopleChanged('teachers') && (
                                            <div className={styles.cardActions}>
                                                <Button appearance="subtle" disabled={!!savingSection} onClick={() => {
                                                    setModule(prev => ({ ...prev, teachers: structuredClone(savedModule.teachers) }))
                                                    setTeacherSearch('')
                                                }}>Annulla</Button>
                                                {saveButton('teachers', true)}
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.modulesContent}>
                                        <div className={styles.peopleSearchBox}>
                                            <TextInput appearance="modern"
                                                type="search"
                                                placeholder="Cerca un docente"
                                                value={teacherSearch}
                                                onKeyUp={(value) => {
                                                    setTeacherSearch(value)
                                                }}
                                            />
                                            {teacherSearch.trim() !== '' && (
                                                <div className={styles.searchResultsList}>
                                                    <Each of={filteredTeachers.sort((a, b) => a.surname.localeCompare(b.surname)).slice(0, 8)} render={(teacher) => {
                                                        const alreadyAdded = module.teachers.some((t) => t.id === teacher.id)
                                                        return (
                                                            <div
                                                                className={`${styles.searchResultItem} ${alreadyAdded ? styles.searchResultDisabled : ''}`}
                                                                onClick={() => {
                                                                    addTeacherToModule(teacher)
                                                                }}
                                                            >
                                                                <img src={teacher.picture} alt='teacher-avatar' className={styles.teacherSearchPicture} />
                                                                <div className={styles.teacherSearchName}>{teacher.surname} <span>{teacher.name}</span> </div>
                                                                <div className={styles.spacer} />
                                                                {alreadyAdded && <div className={styles.alreadyAdded}>GIA PRESENTE</div>}
                                                            </div>
                                                        )
                                                    }} />
                                                </div>
                                            )}
                                            {loadingTeacherResults && <div className={styles.loadingTeachers}>Caricando i docenti...</div>}
                                        </div>
                                        <div className={styles.teachers}>
                                            <Each of={module.teachers} render={(teacher) => {
                                                return (
                                                    <div className={styles.teacher}>
                                                        <div className={styles.teacherAvatarWrapper}>
                                                            <img src={teacher.picture} alt='teacher-avatar' className={styles.teacherPicture} onClick={() => navigate(`/users/teachers/${teacher.id}`)} />
                                                            <button
                                                                className={styles.removeTeacherButton}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    removeTeacherFromModule(teacher.id)
                                                                }}
                                                            >
                                                                <CloseIcon />
                                                            </button>
                                                        </div>
                                                        <div className={typo.headline}>{teacher.name} {teacher.surname}</div>
                                                    </div>
                                                )
                                            }} />
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.modules}>
                                    <div className={styles.modulesHeader}>
                                        <div className={styles.sectionTitle}>Tutor · {module.tutors.length}</div>
                                        {moduleId !== 'create' && peopleChanged('tutors') && (
                                            <div className={styles.cardActions}>
                                                <Button appearance="subtle" disabled={!!savingSection} onClick={() => {
                                                    setModule(prev => ({ ...prev, tutors: structuredClone(savedModule.tutors) }))
                                                    setTutorSearch('')
                                                }}>Annulla</Button>
                                                {saveButton('tutors', true)}
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.modulesContent}>
                                        <div className={styles.peopleSearchBox}>
                                            <TextInput appearance="modern"
                                                type="search"
                                                placeholder="Cerca un tutor"
                                                value={tutorSearch}
                                                onKeyUp={(value) => {
                                                    setTutorSearch(value)
                                                }}
                                            />
                                            {tutorSearch.trim() !== '' && (
                                                <div className={styles.searchResultsList}>
                                                    <Each of={filteredTutors.sort((a, b) => a.surname.localeCompare(b.surname)).slice(0, 8)} render={(tutor) => {
                                                        const alreadyAdded = module.tutors.some((t) => t.id === tutor.id)
                                                        return (
                                                            <div
                                                                className={`${styles.searchResultItem} ${alreadyAdded ? styles.searchResultDisabled : ''}`}
                                                                onClick={() => {
                                                                    addTutorToModule(tutor)
                                                                }}
                                                            >
                                                                <img src={tutor.picture} alt='tutor-avatar' className={styles.teacherSearchPicture} />
                                                                <div className={styles.teacherSearchName}>{tutor.surname} <span>{tutor.name}</span> </div>
                                                                <div className={styles.spacer} />
                                                                {alreadyAdded && <div className={styles.alreadyAdded}>GIA PRESENTE</div>}
                                                            </div>
                                                        )
                                                    }} />
                                                </div>
                                            )}
                                            {loadingTutorResults && <div className={styles.loadingTeachers}>Caricando i tutor...</div>}
                                        </div>
                                        <div className={styles.teachers}>
                                            <Each of={module.tutors} render={(tutor) => {
                                                return (
                                                    <div className={styles.teacher}>
                                                        <div className={styles.teacherAvatarWrapper}>
                                                            <img src={tutor.picture} alt='tutor-avatar' className={styles.teacherPicture} onClick={() => navigate(`/users/teachers/${tutor.id}`)} />
                                                            <button
                                                                className={styles.removeTeacherButton}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    removeTutorFromModule(tutor.id)
                                                                }}
                                                            >
                                                                <CloseIcon />
                                                            </button>
                                                        </div>
                                                        <div className={typo.headline}>{tutor.name} {tutor.surname}</div>
                                                    </div>
                                                )
                                            }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </div >
            <AlertDialog
                open={alert.open}
                title={alert.title}
                text={alert.text}
                actions={alert.actions}
                status={alert.status}
                onClose={() => {
                    setAlert((prev) => {
                        prev.open = false
                        return { ...prev }
                    })
                }}
            />

            <Dialog open={ordering} appearance="modern" title="Riordina lezioni"
                style={{ maxWidth: '680px' }}
                onClose={() => { if (!savingOrder) setOrdering(false) }}
                action={
                    <div className={styles.reorderActions}>
                    <Button appearance="subtle" disabled={savingOrder} onClick={() => setOrdering(false)}>Annulla</Button>
                    <Button
                        accentColor="var(--primary)"
                        status={savingOrder ? ButtonStatus.Loading : null}
                        disabled={savingOrder}
                        onClick={async () => {
                            setSavingOrder(true)
                            try {
                                await api.put('/admin/lessons/reorder', { ordered_ids: orderedLessons.map(c => c.id) })
                                setOrdering(false)
                                setSortedLessons((prev) => (
                                    // aggiorno le position di sortedLessons seguendo gli id ordinati in orderedLessons
                                    prev.map(lesson => {
                                        lesson.position = orderedLessons.findIndex(ol => ol.id === lesson.id) + 1
                                        return lesson
                                    })
                                ))
                            }
                            catch (e) {
                                console.error(e)
                            }
                            setSavingOrder(false)
                        }}>Salva ordine</Button>
                    </div>
                }
            >
                <div className={styles.dialogContent}>
                    <p className={styles.reorderHint}>Trascina le lezioni nell’ordine desiderato, poi salva le modifiche.</p>
                    <ReactSortable
                        className={styles.reorderList}
                        list={orderedLessons}
                        setList={setOrderedLessons}
                        animation={180}
                        disabled={savingOrder}
                        ghostClass={styles.sortableGhost}
                        chosenClass={styles.sortableChosen}
                    >
                        {orderedLessons.map((lesson, index) => (
                            <div key={lesson.id} className={styles.sortableLesson}>
                                <svg className={styles.dragHandle} width="20" height="24" viewBox="0 0 20 24" fill="currentColor" aria-hidden="true">
                                    {[6, 12, 18].map(y => <g key={y}><circle cx="7" cy={y} r="1.5" /><circle cx="13" cy={y} r="1.5" /></g>)}
                                </svg>
                                <span className={styles.lessonPosition}>{index + 1}</span>
                                <span className={styles.sortableName}>{lesson.name}</span>
                            </div>
                        ))}
                    </ReactSortable>
                </div>
            </Dialog>

            <Dialog
                open={lesson}
                appearance="modern"
                title={lesson?.id ? 'Modifica lezione' : 'Nuova lezione'}
                onClose={() => {
                    setLesson(null)
                }}>
                <LessonEditor onCancel={() => setLesson(null)} lesson={lesson} module_id={moduleId} onDelete={() => { setLessonDelete(lesson.id) }} onSave={(res, { applyThumbnailToEdition = false } = {}) => {
                    setModule((prev) => {
                        const exists = prev.lessons.some((currentLesson) => currentLesson.id === res.id)
                        const updatedLessons = prev.lessons.map((currentLesson) => {
                            if (currentLesson.id === res.id) return { ...currentLesson, ...res }
                            return applyThumbnailToEdition ? { ...currentLesson, thumbnail: res.thumbnail } : currentLesson
                        })
                        if (!exists) updatedLessons.push(res)
                        return {
                            ...prev,
                            lessons: updatedLessons,
                            duration: getModuleDurationSeconds(updatedLessons)
                        }
                    })
                    setLesson(null)
                }} />
            </Dialog>
            <AlertDialog
                open={lessonDelete}
                title={"Sei sicuro di voler eliminare la lezione?"}
                text={"Questa operazione non può essere annullata."}
                actions={[
                    {
                        label: "ANNULLA",
                        onClick: () => { setLessonDelete(null) }
                    },
                    {
                        label: "ELIMINA",
                        accentColor: "var(--sf-red)",
                        onClick: async () => {
                            await deleteLesson(lessonDelete, false)
                            setLessonDelete(null)
                        }
                    }
                ]}
                status={DialogStatus.Warning}
                onClose={() => {
                    setLessonDelete(null)
                }}
            />
            <AlertDialog
                open={forceDeleteLesson}
                title={"Eliminare i dati storici?"}
                text={"Sono presenti dati storici degli utenti che hanno partecipato alla lezione. Sei sicuro di voler eliminare la lezione?"}
                actions={[
                    {
                        label: "ANNULLA",
                        onClick: () => { setForceDeleteLesson(null) }
                    },
                    {
                        label: "ELIMINA",
                        accentColor: "var(--sf-red)",
                        confirmText: "elimina",
                        onClick: async () => {
                            await deleteLesson(forceDeleteLesson, true)
                            setForceDeleteLesson(null)
                            setLessonDelete(null)
                            setModule((prev) => {
                                prev.lessons = prev.lessons.filter(l => l.id !== lesson.id)
                                prev.duration = getModuleDurationSeconds(prev.lessons)
                                return { ...prev }
                            })
                            setLesson(null)
                        }
                    }
                ]}
                status={DialogStatus.Warning}
                onClose={() => {
                    setForceDeleteLesson(null)
                }}
            />

        </HeaderFooterLayout >
    )

}

export default ModuleEditor
